/**
 * Scoping and collapse: the layer between the contract graph and the layout.
 *
 * The renderer was built against a fixture of one company's neighbourhood. Real
 * answers are not that shape — the portfolio report carries 8 companies, 28
 * documents and 43 metrics over 238 edges, and drawing all of it produces a
 * tall thread that `fitView` shrinks to unreadability. The mockup shows ONE
 * company's neighbourhood with a handful of metrics, and that is a property of
 * the VIEW, not of the data: the graph is complete, and this module decides how
 * much of it a reader is shown at once.
 *
 * Four rules, applied in order, all pure and deterministic:
 *
 *   1. narrow to the focus company's neighbourhood,
 *   1b. merge figures that are the same figure cited twice,
 *   2. fold plainly-cited figures into one card per source document,
 *   3. fold clauses that back nothing kept into one card per source document,
 *   4. in room scope, fold each company's documents into one card PER TYPE.
 *
 * Rule 1b is an addition to the ticket's four, and it earns its place: the EBITDA
 * walk cites "Management-adjusted EBITDA · 26,500" from two versions of the same
 * reporting pack, so the graph carries it twice and the canvas drew it twice.
 * Two cards with the same name and the same number are not two facts.
 *
 * Everything a rule hides is listed in the synthetic node's `meta.collapsed`,
 * so nothing is lost: the side panel can name the hidden nodes and clicking the
 * card puts its id in `expanded` and brings them back.
 */

import type {
	ContextEdge,
	ContextGraph,
	ContextNode,
	ContextRelation,
} from "../../types/contextGraph";

export interface CollapseOptions {
	/** Node id to narrow to. Only a `company` node narrows anything. */
	focus: string | null;
	/** Synthetic node ids the reader has opened. */
	expanded: ReadonlySet<string>;
	/** Skip rules 2–4 entirely. Rule 1 still applies. */
	expandAll: boolean;
}

/** Relations that attach a document to the company it is about, either way. */
const DOCUMENT_LINKS: readonly ContextRelation[] = [
	"GOVERNED_BY",
	"REPORTS",
	"CERTIFIES",
	"ABOUT",
];

/**
 * Relations that carry the answer itself — the chain from a document or a
 * company down to the figures, clauses and covenants an answer cites.
 */
const ANSWER_LINKS: readonly ContextRelation[] = [
	"HAS_METRIC",
	"SOURCE",
	"DEFINES",
	"SETS_BASIS",
	"ADD_BACKS",
	"COMPUTES",
	"TESTED_WITH",
	"SUBJECT_TO",
	"RESULTS_IN",
	"EXCLUDED",
	"HEADROOM",
	"UNDER",
	"ABOUT",
];

/** Relations by which a document supplies a figure, clause or covenant. */
const SOURCE_LINKS: readonly ContextRelation[] = [
	"SOURCE",
	"REPORTS",
	"CERTIFIES",
	"ADD_BACKS",
];

/** The answer layer: kinds rule 1 collects by traversal rather than by adjacency. */
const ANSWER_KINDS: readonly ContextNode["kind"][] = [
	"clause",
	"metric",
	"covenant",
	"status",
];

/**
 * Relations that make a metric part of the reasoning rather than a figure that
 * merely got cited. A metric with any of these is never folded away.
 */
const LOAD_BEARING: readonly ContextRelation[] = [
	"COMPUTES",
	"SETS_BASIS",
	"TESTED_WITH",
	"RESULTS_IN",
	"DEFINES",
	// A schedule line the answer placed in or out of the basis IS the walk.
	"ADD_BACKS",
	"EXCLUDED",
	"HEADROOM",
	"UNDER",
];

/**
 * A group of one is not a group: folding a single card into `+1 cited figures`
 * costs the reader its label and saves nothing.
 */
const MIN_GROUP = 2;

interface Working {
	nodes: ContextNode[];
	edges: ContextEdge[];
}

function has<T>(list: readonly T[], value: T): boolean {
	return list.includes(value);
}

function adjacency(edges: readonly ContextEdge[]): Map<string, ContextEdge[]> {
	const map = new Map<string, ContextEdge[]>();
	const add = (id: string, edge: ContextEdge) => {
		const list = map.get(id);
		if (list) list.push(edge);
		else map.set(id, [edge]);
	};
	for (const edge of edges) {
		add(edge.src, edge);
		add(edge.dst, edge);
	}
	return map;
}

/** The other end of an edge from `id`. */
function other(edge: ContextEdge, id: string): string {
	return edge.src === id ? edge.dst : edge.src;
}

/** Drop every edge with an endpoint that is no longer present. */
function pruneEdges(working: Working): Working {
	const ids = new Set(working.nodes.map((node) => node.id));
	return {
		nodes: working.nodes,
		edges: working.edges.filter((edge) => ids.has(edge.src) && ids.has(edge.dst)),
	};
}

// ---------------------------------------------------------------------------
// Rule 1 — the focus company's neighbourhood
// ---------------------------------------------------------------------------

/**
 * Everything the focus company's story needs: the company, its fund, its
 * periods, the documents about it (and their superseded drafts), and the
 * clauses, metrics, covenants and statuses reachable from any of those through
 * the answer layer.
 *
 * The traversal deliberately never re-enters a company or a document. A
 * document shared between two borrowers would otherwise be a bridge into the
 * whole portfolio, which is the blow-up this rule exists to prevent.
 */
export function neighbourhoodOf(
	graph: Pick<ContextGraph, "nodes" | "edges">,
	focus: string,
): Set<string> | null {
	const byId = new Map(graph.nodes.map((node) => [node.id, node]));
	const company = byId.get(focus);
	if (!company || company.kind !== "company") return null;

	const incident = adjacency(graph.edges);
	const keep = new Set<string>([focus]);
	const documents: string[] = [];

	for (const edge of incident.get(focus) ?? []) {
		const neighbour = byId.get(other(edge, focus));
		if (!neighbour) continue;
		if (neighbour.kind === "fund" || neighbour.kind === "period") {
			keep.add(neighbour.id);
		} else if (neighbour.kind === "document" && has(DOCUMENT_LINKS, edge.relation)) {
			keep.add(neighbour.id);
			documents.push(neighbour.id);
		}
	}

	const queue = [focus, ...documents];
	const answerNodes: string[] = [];
	while (queue.length > 0) {
		const current = queue.shift() as string;
		for (const edge of incident.get(current) ?? []) {
			if (!has(ANSWER_LINKS, edge.relation)) continue;
			const neighbour = byId.get(other(edge, current));
			if (!neighbour || keep.has(neighbour.id)) continue;
			if (!has(ANSWER_KINDS, neighbour.kind)) continue;
			keep.add(neighbour.id);
			answerNodes.push(neighbour.id);
			queue.push(neighbour.id);
		}
	}

	// The documents the answer layer was read out of.
	//
	// A document does not always hang off the company — it can attach only to
	// the figure it sourced (`doc —REPORTS→ metric`), which is how the hand-
	// written fixture is wired and how a citation-derived graph naturally comes
	// out. Without this pass the neighbourhood keeps the figures but throws away
	// the papers they came from, which is the opposite of the point.
	const sourced: string[] = [];
	for (const id of answerNodes) {
		for (const edge of incident.get(id) ?? []) {
			if (!has(SOURCE_LINKS, edge.relation)) continue;
			// Only the citing end: `document —SOURCE→ metric`, never the reverse.
			if (edge.dst !== id) continue;
			const document = byId.get(edge.src);
			if (!document || document.kind !== "document" || keep.has(document.id)) continue;
			keep.add(document.id);
			sourced.push(document.id);
		}
	}

	for (const document of [...documents, ...sourced]) {
		for (const edge of incident.get(document) ?? []) {
			if (edge.relation !== "SUPERSEDES") continue;
			const draft = byId.get(other(edge, document));
			if (draft && draft.kind === "document") keep.add(draft.id);
		}
	}

	return keep;
}

function applyNeighbourhood(working: Working, focus: string | null): Working {
	if (focus === null) return working;
	const keep = neighbourhoodOf(working, focus);
	if (keep === null) return working;
	return pruneEdges({
		nodes: working.nodes.filter((node) => keep.has(node.id)),
		edges: working.edges,
	});
}

// ---------------------------------------------------------------------------
// Rule 1b — the same figure cited twice
// ---------------------------------------------------------------------------

/**
 * What makes two metric cards the same fact: the same company's figure, with
 * the same name, the same value and the same role.
 *
 * Deliberately strict. Two "Interest expense" rows at different values are two
 * facts and both stay; it is only the identical pair — same label, same value —
 * that is one fact the citation layer happened to reach twice.
 */
function metricIdentity(
	metric: ContextNode,
	incident: Map<string, ContextEdge[]>,
	byId: Map<string, ContextNode>,
): string {
	const company = owningCompanyOf(metric.id, incident, byId);
	return [company?.id ?? "", metric.label, metric.sublabel ?? "", metric.variant ?? ""].join(
		"\u0000",
	);
}

export function mergeDuplicateMetrics(working: Working): Working {
	const byId = new Map(working.nodes.map((node) => [node.id, node]));
	const incident = adjacency(working.edges);

	const survivorOf = new Map<string, string>();
	const merged = new Map<string, string[]>();
	const firstSeen = new Map<string, string>();

	for (const node of working.nodes) {
		if (node.kind !== "metric") continue;
		const identity = metricIdentity(node, incident, byId);
		const survivor = firstSeen.get(identity);
		if (survivor === undefined) {
			firstSeen.set(identity, node.id);
			continue;
		}
		survivorOf.set(node.id, survivor);
		merged.set(survivor, [...(merged.get(survivor) ?? []), node.id]);
	}

	if (survivorOf.size === 0) return working;

	const nodes = working.nodes
		.filter((node) => !survivorOf.has(node.id))
		.map((node) => {
			const absorbed = merged.get(node.id);
			// Recorded, not hidden: the survivor says which cards it stands for.
			return absorbed ? { ...node, meta: { ...node.meta, merged: absorbed } } : node;
		});

	// Every edge that pointed at a duplicate now points at the survivor, so the
	// second document that cited the figure keeps its SOURCE edge.
	const seen = new Set<string>();
	const edges: ContextEdge[] = [];
	for (const edge of working.edges) {
		const src = survivorOf.get(edge.src) ?? edge.src;
		const dst = survivorOf.get(edge.dst) ?? edge.dst;
		if (src === dst) continue;
		const key = `${src}\u0000${dst}\u0000${edge.relation}`;
		if (seen.has(key)) continue;
		seen.add(key);
		edges.push(src === edge.src && dst === edge.dst ? edge : { ...edge, src, dst });
	}

	return pruneEdges({ nodes, edges });
}

// ---------------------------------------------------------------------------
// Rules 2 and 3 — fold what a document merely contributed
// ---------------------------------------------------------------------------

/** The document a node was cited from, via `document —SOURCE→ node`. */
function sourceDocumentOf(
	id: string,
	incident: Map<string, ContextEdge[]>,
	byId: Map<string, ContextNode>,
): ContextNode | null {
	for (const edge of incident.get(id) ?? []) {
		if (edge.relation !== "SOURCE" || edge.dst !== id) continue;
		const document = byId.get(edge.src);
		if (document && document.kind === "document") return document;
	}
	return null;
}

/** The company a metric hangs off, via `company —HAS_METRIC→ metric`. */
function owningCompanyOf(
	id: string,
	incident: Map<string, ContextEdge[]>,
	byId: Map<string, ContextNode>,
): ContextNode | null {
	for (const edge of incident.get(id) ?? []) {
		if (edge.relation !== "HAS_METRIC" || edge.dst !== id) continue;
		const company = byId.get(edge.src);
		if (company && company.kind === "company") return company;
	}
	return null;
}

interface FoldGroup {
	syntheticId: string;
	document: ContextNode;
	members: ContextNode[];
}

/**
 * Fold each group into one synthetic card, unless the reader has opened it.
 *
 * `build` turns a group into its card and the edges that reattach it; the
 * members and every edge they carried are then dropped, which is safe because a
 * node only becomes a member if nothing else depended on it.
 */
function foldGroups(
	working: Working,
	groups: FoldGroup[],
	expanded: ReadonlySet<string>,
	build: (group: FoldGroup) => { node: ContextNode; edges: ContextEdge[] },
): Working {
	const folded = groups.filter(
		(group) => group.members.length >= MIN_GROUP && !expanded.has(group.syntheticId),
	);
	if (folded.length === 0) return working;

	const hidden = new Set<string>();
	const added: ContextNode[] = [];
	const addedEdges: ContextEdge[] = [];
	for (const group of folded) {
		for (const member of group.members) hidden.add(member.id);
		const { node, edges } = build(group);
		added.push(node);
		addedEdges.push(...edges);
	}

	return pruneEdges({
		nodes: [...working.nodes.filter((node) => !hidden.has(node.id)), ...added],
		edges: [
			...working.edges.filter((edge) => !hidden.has(edge.src) && !hidden.has(edge.dst)),
			...addedEdges,
		],
	});
}

/** Group nodes by the document they were cited from, in contract order. */
function groupBySourceDocument(
	candidates: ContextNode[],
	suffix: "metrics" | "clauses",
	incident: Map<string, ContextEdge[]>,
	byId: Map<string, ContextNode>,
): FoldGroup[] {
	const groups = new Map<string, FoldGroup>();
	for (const node of candidates) {
		const document = sourceDocumentOf(node.id, incident, byId);
		// Without a source document there is nothing to group under, and no
		// honest label for the card. Keep the node as it is.
		if (document === null) continue;
		const syntheticId = `more:${document.id}:${suffix}`;
		const group = groups.get(syntheticId);
		if (group) group.members.push(node);
		else groups.set(syntheticId, { syntheticId, document, members: [node] });
	}
	return [...groups.values()];
}

function collapseMetrics(working: Working, expanded: ReadonlySet<string>): Working {
	const byId = new Map(working.nodes.map((node) => [node.id, node]));
	const incident = adjacency(working.edges);

	const plainlyCited = working.nodes.filter((node) => {
		if (node.kind !== "metric") return false;
		if (node.variant) return false;
		return !(incident.get(node.id) ?? []).some((edge) =>
			has(LOAD_BEARING, edge.relation),
		);
	});

	const groups = groupBySourceDocument(plainlyCited, "metrics", incident, byId);

	return foldGroups(working, groups, expanded, (group) => {
		const company = owningCompanyOf(group.members[0].id, incident, byId);
		const node: ContextNode = {
			id: group.syntheticId,
			kind: "metric",
			label: `+${group.members.length} cited figures`,
			sublabel: group.document.label,
			status: null,
			badge: null,
			variant: null,
			href: null,
			meta: { collapsed: group.members.map((member) => member.id) },
		};
		const edges: ContextEdge[] = [
			{
				id: `${group.syntheticId}:source`,
				src: group.document.id,
				dst: node.id,
				relation: "SOURCE",
				on_path: false,
				label: null,
				meta: {},
			},
		];
		if (company) {
			edges.push({
				id: `${group.syntheticId}:has-metric`,
				src: company.id,
				dst: node.id,
				relation: "HAS_METRIC",
				on_path: false,
				label: null,
				meta: {},
			});
		}
		return { node, edges };
	});
}

function collapseClauses(working: Working, expanded: ReadonlySet<string>): Working {
	const byId = new Map(working.nodes.map((node) => [node.id, node]));
	const incident = adjacency(working.edges);

	// "Kept" means kept after the metric fold — a clause whose only basis was a
	// figure that has just been folded away is backing nothing the reader sees.
	const backsSomething = (clause: ContextNode): boolean =>
		(incident.get(clause.id) ?? []).some((edge) => {
			if (edge.relation !== "DEFINES" && edge.relation !== "SETS_BASIS") return false;
			const target = byId.get(other(edge, clause.id));
			return (
				target !== undefined && (target.kind === "metric" || target.kind === "covenant")
			);
		});

	const spare = working.nodes.filter(
		(node) => node.kind === "clause" && !backsSomething(node),
	);
	const groups = groupBySourceDocument(spare, "clauses", incident, byId);

	return foldGroups(working, groups, expanded, (group) => ({
		node: {
			id: group.syntheticId,
			kind: "clause",
			label: `+${group.members.length} clauses`,
			sublabel: group.document.label,
			status: null,
			badge: null,
			variant: null,
			href: null,
			meta: { collapsed: group.members.map((member) => member.id) },
		},
		edges: [
			{
				id: `${group.syntheticId}:source`,
				src: group.document.id,
				dst: group.syntheticId,
				relation: "SOURCE",
				on_path: false,
				label: null,
				meta: {},
			},
		],
	}));
}

// ---------------------------------------------------------------------------
// Rule 4 — one Documents card per company, in room scope
// ---------------------------------------------------------------------------

/**
 * What a folded stack of documents is made of — "Compliance Certificate,
 * Credit Agreement, +1".
 *
 * By TYPE, not by file name. A group card answers "what kind of paperwork is in
 * here"; the file names are the identity of the individual cards inside it, and
 * they are what the reader gets when they open it. Summarising by name instead
 * made six companies' stacks read as six copies of one long identical string,
 * which is the same failure the type-labelled document cards had.
 *
 * Counts DISTINCT types, so "+N" is how many more kinds there are, not how many
 * more files — two compliance certificates are one kind of thing.
 */
export function documentSummary(documents: readonly ContextNode[]): string {
	const kinds: string[] = [];
	for (const document of documents) {
		const type =
			typeof document.meta.doc_type_label === "string"
				? document.meta.doc_type_label
				: document.label;
		if (!kinds.includes(type)) kinds.push(type);
	}
	const shown = kinds.slice(0, 2);
	const rest = kinds.length - shown.length;
	return rest > 0 ? `${shown.join(", ")}, +${rest}` : shown.join(", ");
}

/**
 * The document's type, as the projection recorded it.
 *
 * Three sources, in descending order of how much the backend has already done:
 * the short display title it sends, the raw `doc_type` (humanised here), and
 * the node's `variant`. The raw fallbacks matter — a payload captured before
 * `doc_type_label` existed still carries `doc_type`, and without them every
 * document in such a payload falls into one bucket called "Document", which is
 * the bag this rule exists to get rid of.
 */
function docTypeOf(node: ContextNode): string {
	if (typeof node.meta.doc_type_label === "string" && node.meta.doc_type_label) {
		return node.meta.doc_type_label;
	}
	const raw =
		typeof node.meta.doc_type === "string" && node.meta.doc_type
			? node.meta.doc_type
			: typeof node.variant === "string" && node.variant
				? node.variant
				: null;
	return raw ? humaniseType(raw) : node.label || "Document";
}

/** `"compliance_certificate"` -> `"Compliance Certificate"`. */
function humaniseType(raw: string): string {
	return raw
		.split(/[_\-\s]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

/** Slug for a type, so the synthetic node's id is stable across polls. */
function typeSlug(type: string): string {
	return type.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "other";
}

/**
 * "8 files · 8 companies" — how much paperwork of this kind, and how widely.
 *
 * The two numbers together are the point: eight files across eight borrowers is
 * a complete set, eight files across six borrowers means two are missing one,
 * and the reader sees which two by following the edges.
 */
export function typeGroupSummary(
	documents: readonly ContextNode[],
	owners: number,
): string {
	const files = `${documents.length} file${documents.length === 1 ? "" : "s"}`;
	if (owners <= 0) return files;
	return `${files} · ${owners} compan${owners === 1 ? "y" : "ies"}`;
}

/**
 * Rule 4 — one card per DOCUMENT TYPE for the whole room.
 *
 * A room is read as "which of my borrowers has filed what". Two shapes were
 * tried before this one and both failed on the real catalog:
 *
 *   - one `Documents · N` card per company put eight identical labels in a
 *     column and made the reader open every one to find the gap;
 *   - one card per type PER COMPANY was honest but 28 cards tall — a 2846px
 *     column that `fitView` shrinks to a 3.6px typeface.
 *
 * So the types are shared. There is ONE "Credit Agreement" card in the room and
 * every borrower governed by one points at it. That is the question a lender
 * actually asks — who has filed a compliance certificate this quarter, and who
 * has not — and it is answered by looking at which companies have an edge, with
 * no clicking at all. Opening the card lists every file behind it, each named
 * and attributed to its company.
 *
 * A shared card is a VIEW, not a claim that the files are the same: each
 * company still owns its own document node underneath, and expanding restores
 * exactly those nodes and their own edges.
 *
 * Superseded drafts are absorbed and counted on the badge.
 */
function collapseRoomDocuments(working: Working, expanded: ReadonlySet<string>): Working {
	const byId = new Map(working.nodes.map((node) => [node.id, node]));
	const incident = adjacency(working.edges);

	const hidden = new Set<string>();
	const added: ContextNode[] = [];
	const addedEdges: ContextEdge[] = [];

	const owners = working.nodes.filter(
		(node) => node.kind === "company" || node.kind === "fund",
	);

	interface TypeGroup {
		type: string;
		members: ContextNode[];
		drafts: ContextNode[];
		/** owner id -> the relation that owner had with this kind of paperwork. */
		links: Map<string, ContextEdge["relation"]>;
	}

	// Built in node order so the same room always draws the same cards in the
	// same order.
	const groups = new Map<string, TypeGroup>();

	for (const owner of owners) {
		for (const edge of incident.get(owner.id) ?? []) {
			if (!has(DOCUMENT_LINKS, edge.relation)) continue;
			const document = byId.get(other(edge, owner.id));
			if (!document || document.kind !== "document") continue;
			// A draft reached through SUPERSEDES is folded in below, not here.
			if (document.status === "superseded") continue;

			const type = docTypeOf(document);
			let group = groups.get(type);
			if (!group) {
				group = { type, members: [], drafts: [], links: new Map() };
				groups.set(type, group);
			}
			if (!group.members.includes(document)) group.members.push(document);
			if (!group.links.has(owner.id)) group.links.set(owner.id, edge.relation);
		}
	}

	for (const group of groups.values()) {
		const syntheticId = `doctype:${typeSlug(group.type)}`;
		if (expanded.has(syntheticId)) continue;

		for (const document of group.members) {
			for (const edge of incident.get(document.id) ?? []) {
				if (edge.relation !== "SUPERSEDES") continue;
				const draft = byId.get(other(edge, document.id));
				if (draft && draft.kind === "document" && !group.members.includes(draft)) {
					group.drafts.push(draft);
				}
			}
		}

		for (const node of [...group.members, ...group.drafts]) hidden.add(node.id);
		added.push({
			id: syntheticId,
			kind: "document",
			label: group.type,
			sublabel: typeGroupSummary(group.members, group.links.size),
			status: null,
			badge:
				group.drafts.length > 0 ? `${group.drafts.length} superseded` : null,
			// The type card IS the type, so it takes the type's accent.
			variant: group.members[0].variant ?? null,
			href: null,
			meta: {
				doc_type_label: group.type,
				collapsed: [...group.members, ...group.drafts].map((node) => node.id),
			},
		});

		for (const [ownerId, relation] of group.links) {
			// The relation the owner actually had with this paperwork, not a
			// blanket REPORTS: a credit agreement GOVERNS its borrower and a
			// certificate CERTIFIES it, and the edge is what says which.
			const backwards = relation === "CERTIFIES" || relation === "ABOUT";
			addedEdges.push({
				id: `${syntheticId}:${relation.toLowerCase()}:${ownerId}`,
				src: backwards ? syntheticId : ownerId,
				dst: backwards ? ownerId : syntheticId,
				relation,
				on_path: false,
				label: null,
				meta: {},
			});
		}
	}

	if (added.length === 0) return working;

	return pruneEdges({
		nodes: [...working.nodes.filter((node) => !hidden.has(node.id)), ...added],
		edges: [
			...working.edges.filter((edge) => !hidden.has(edge.src) && !hidden.has(edge.dst)),
			...addedEdges,
		],
	});
}

// ---------------------------------------------------------------------------

/** True for a node this module invented, rather than one the backend sent. */
export function isCollapsedNode(node: Pick<ContextNode, "meta">): boolean {
	return Array.isArray(node.meta.collapsed);
}

/** The ids a synthetic node stands in for. Empty for a real node. */
export function collapsedIds(node: Pick<ContextNode, "meta">): string[] {
	const ids = node.meta.collapsed;
	return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
}

/** Every company in the graph, in label order — the focus control's options. */
export function companiesIn(graph: Pick<ContextGraph, "nodes">): ContextNode[] {
	return graph.nodes
		.filter((node) => node.kind === "company")
		.sort((a, b) => a.label.localeCompare(b.label));
}

export function collapseGraph(
	graph: ContextGraph,
	options: CollapseOptions,
): ContextGraph {
	let working: Working = { nodes: graph.nodes, edges: graph.edges };

	working = applyNeighbourhood(working, options.focus);
	// Before the folding, so a duplicate cannot skew which document a group of
	// figures is attributed to.
	working = mergeDuplicateMetrics(working);

	if (!options.expandAll) {
		working = collapseMetrics(working, options.expanded);
		working = collapseClauses(working, options.expanded);
		if (graph.scope === "room") {
			working = collapseRoomDocuments(working, options.expanded);
		}
	}

	working = pruneEdges(working);
	return { ...graph, focus: options.focus, nodes: working.nodes, edges: working.edges };
}
